import { getI18n } from '@/helpers/i18n';
import dayjs, { Dayjs } from 'dayjs';

export const formRules = {
    /** Value is required: */
    required: (value: unknown) => !!value || getI18n().t('value_please'),

    /** Must be an email address: */
    email: (value: string) => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return value ? pattern.test(value) || getI18n().t('invalid_email') : true;
    },

    /** Only alpha-chars */
    charsOnly: (value: string) => {
        const antiPattern = /[0-9]/;
        return value ? !antiPattern.test(value) || getI18n().t('chars_only') : true;
    },

    /**
     * Creator function: use with formRules.minLength(8) to get a min-8-length validator function:
     */
    minLength: (minLength: number, ignoreWhitespace?: boolean) => (value: string) => {
        if (value && ignoreWhitespace) {
            value = value.replaceAll(' ', '');
        }
        return (value || '').length >= minLength || getI18n().t('min_chars', { chars: minLength });
    },
    /**
     * Creator function: use with formRules.maxLength(10) to get a max-10-length validator function:
     */
    maxLength: (maxLength: number, ignoreWhitespace?: boolean) => (value: string) => {
        if (value && ignoreWhitespace) {
            value = value.replaceAll(' ', '');
        }
        return (value || '').length <= maxLength || `${value.length}/${maxLength}`;
    },

    /**
     * Creator function: use with formRules.between(1000,2000) to allow numbers between 1000 and 2000
     */
    between: (min: number | null, max: number | null, errorMsg?: string | null) => (value: number | null) => {
        if (min === null && max === null) {
            return true;
        }
        if (min === null && max !== null) {
            return value === null || value <= max ? true : errorMsg || getI18n().t('value_max', { max });
        }
        if (max === null && min !== null) {
            return value === null || value >= min ? true : errorMsg || getI18n().t('value_min', { min });
        }
        return value === null || (value >= (min as number) && value <= (max as number))
            ? true
            : errorMsg || getI18n().t('value_between', { min, max });
    },

    /**
     *  UID Number in the format like CHE-106.050.103
     */
    uid: (value: string) => {
        const pattern = /^CHE-[0-9][0-9][0-9]\.[0-9][0-9][0-9]\.[0-9][0-9][0-9]$/;
        return value ? pattern.test(value) || getI18n().t('invalid_uid') : true;
    },

    dateFormat: (dateStr: string) => (value: string | Dayjs) => {
        return !value || dayjs(value, dateStr, true).isValid() || getI18n().t('wrong_format', { format: dateStr });
    },

    isInt: () => (value: string) => (value ? /^\d*$/.test(value) || getI18n().t('int_only') : true),

    houseNumber: (value: string) => {
        const pattern = /^[0-9a-zA-Z]*\.?[0-9a-zA-Z]*$/;
        return value ? pattern.test(value) || getI18n().t('invalid_house_number') : true;
    },

    iban: (value: string) => {
        if (!value) {
            return false;
        }
        if (value.startsWith('CH') || value.startsWith('LI')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 21 || `${value.length}/${21}`;
        }
        if (value.startsWith('AT')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 20 || `${value.length}/${20}`;
        }
        if (value.startsWith('DE')) {
            value = value.replaceAll(' ', '');
            return (value || '').length === 22 || `${value.length}/${22}`;
        }
        return true;
    }
};